import React, { useState } from 'react';
import { string, bool } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { propTypes } from '../../../util/types';
import { Form, PrimaryButton, FieldTextInput, IconInquiry, Heading } from '../../../components';

import css from './InquiryForm.module.css';

const ErrorMessage = props => {
  const { error } = props;
  // No transaction process attached to listing
  return error ? (
    <p className={css.error}>
      {error.message === 'No transaction process attached to listing' ? (
        <FormattedMessage id="InquiryForm.sendInquiryErrorNoProcess" />
      ) : (
        <FormattedMessage id="InquiryForm.sendInquiryError" />
      )}
    </p>
  ) : null;
};

const InquiryFormComponent = props => {
  const [images, setImages] = useState([]);

  const handleImageChange = event => {
    const files = Array.from(event.target.files);
    const previewFiles = files.map(file => ({
      id: URL.createObjectURL(file),
      file,
    }));
    setImages([...images, ...previewFiles]);
  };

  const handleRemoveImage = imageId => {
    setImages(images.filter(image => image.id !== imageId));
  };

  const handleFormSubmit = values => {
    // Include the images in the form values
    const updatedValues = {
      ...values,
      images: images.map(image => image.file), // Add the actual file objects to the form values
    };
    props.onSubmit(updatedValues); // Call the parent component's onSubmit handler
  };

  return (
    <FinalForm
      {...props}
      onSubmit={handleFormSubmit} // Use the updated handleFormSubmit function
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          submitButtonWrapperClassName,
          formId,
          handleSubmit,
          inProgress,
          intl,
          listingTitle,
          authorDisplayName,
          sendInquiryError,
        } = fieldRenderProps;

        const messageLabel = intl.formatMessage(
          {
            id: 'InquiryForm.messageLabel',
          },
          { authorDisplayName }
        );
        const messagePlaceholder = intl.formatMessage(
          {
            id: 'InquiryForm.messagePlaceholder',
          },
          { authorDisplayName }
        );
        const messageRequiredMessage = intl.formatMessage({
          id: 'InquiryForm.messageRequired',
        });
        const messageRequired = validators.requiredAndNonEmptyString(messageRequiredMessage);

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = submitInProgress;

        const chatMessageFormatValid = validators.chatMessageFormatValid(
          intl.formatMessage({
            id: 'BlockMessage',
          })
        );

        return (
          <Form
            className={classes}
            onSubmit={handleSubmit}
            enforcePagePreloadFor="OrderDetailsPage"
          >
            <IconInquiry className={css.icon} />
            <Heading as="h2" rootClassName={css.heading}>
              <FormattedMessage id="InquiryForm.heading" values={{ listingTitle }} />
            </Heading>

            {/* Image Previews */}
            {images.length > 0 && (
              <div className={css.imagePreviews}>
                {images.map(image => (
                  <div key={image.id} className={css.imagePreview}>
                    <img loading="lazy" src={image.id} alt="Preview" className={css.previewImage} />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(image.id)}
                      className={css.removeImageButton}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}

            <FieldTextInput
              className={css.field}
              type="textarea"
              name="message"
              id={formId ? `${formId}.message` : 'message'}
              label={messageLabel}
              placeholder={messagePlaceholder}
              validate={validators.composeValidators(messageRequired, chatMessageFormatValid)}
            />

            {/* Add Images Button */}
            <div className={css.addImagesWrapper}>
              <label htmlFor="add-images" className={css.addImagesButton}>
                <FormattedMessage id="InquiryForm.images" />
              </label>
              <input
                id="add-images"
                type="file"
                accept="image/*"
                multiple
                className={css.imageInput}
                onChange={handleImageChange}
              />
            </div>

            <div className={submitButtonWrapperClassName}>
              <ErrorMessage error={sendInquiryError} />

              <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
                <FormattedMessage id="InquiryForm.submitButtonText" />
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

InquiryFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  submitButtonWrapperClassName: null,
  inProgress: false,
  sendInquiryError: null,
};

InquiryFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  submitButtonWrapperClassName: string,

  inProgress: bool,

  listingTitle: string.isRequired,
  authorDisplayName: string.isRequired,
  sendInquiryError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,
};

const InquiryForm = compose(injectIntl)(InquiryFormComponent);

InquiryForm.displayName = 'InquiryForm';

export default InquiryForm;
